/**
 * RevenuePlanZones — the 4-section unified Monetize Plan.
 *
 * Replaces the legacy Opportunity / ICP-Surface / Traction-Map / Momentum
 * stack with conversion-first hypotheses focused on compressing the time
 * to first dollar. Driven by `outputs.revenue_plan.data` (one single LLM
 * generation backed by `monetize_outputs(module='revenue_plan')`).
 *
 *   1. 🔵 ICP            — segments ranked by revenue speed
 *   2. 🔵 Positioning    — conversion-driven angles
 *   3. 🔵 Pricing        — fastest path to first dollar
 *   4. 🔵 Distribution   — highest probability channels
 *
 * Palette: navy `#1E3A8A` to signal the "thinking layer". Plays nicely with
 * the global violet/orange brand, but visually distinct from HQ Execution
 * (which lives in ember red).
 */

import React from 'react';
import {
  Users, Target, Tag, Compass, Sparkles, ArrowUpRight, AlertOctagon,
  TrendingUp, Zap, Loader2, RefreshCw,
} from 'lucide-react';

const NAVY = {
  ink: '#0A1126',
  surface: '#FFFFFF',
  surfaceAlt: '#F7F8FB',
  border: 'rgba(30,58,138,0.14)',
  primary: '#1E3A8A',
  primarySoft: 'rgba(30,58,138,0.08)',
  primaryStrong: '#1E40AF',
  accent: '#3B82F6',
  ok: '#22C55E',
  warn: '#F59E0B',
  bad: '#EF4444',
  text: '#0A1126',
  textDim: 'rgba(10,17,38,0.55)',
  textFaint: 'rgba(10,17,38,0.4)',
};

export default function RevenuePlanZones({ plan, loading, onRegenerate }) {
  if (loading && !plan) {
    return <LoadingState />;
  }
  if (!plan) {
    return <EmptyState />;
  }

  const { icp, positioning, pricing, distribution, first_dollar_thesis } = plan;

  return (
    <div data-testid="revenue-plan-zones" style={{
      display: 'flex', flexDirection: 'column', gap: 22,
    }}>
      <ThesisBanner thesis={first_dollar_thesis} onRegenerate={onRegenerate} />

      <SectionShell
        index="01"
        icon={<Users size={16} />}
        title={icp?.headline || 'Priority customer segments'}
        summary={icp?.summary}
        testid="zone-icp"
      >
        <SegmentList segments={icp?.segments || []} />
      </SectionShell>

      <SectionShell
        index="02"
        icon={<Target size={16} />}
        title={positioning?.headline || 'Conversion-driven positioning'}
        summary={positioning?.summary}
        testid="zone-positioning"
      >
        <AngleList angles={positioning?.angles || []} />
      </SectionShell>

      <SectionShell
        index="03"
        icon={<Tag size={16} />}
        title={pricing?.headline || 'Fastest path to first dollar pricing'}
        summary={pricing?.summary}
        testid="zone-pricing"
      >
        <PricingBlock pricing={pricing || {}} />
      </SectionShell>

      <SectionShell
        index="04"
        icon={<Compass size={16} />}
        title={distribution?.headline || 'Highest probability acquisition channels'}
        summary={distribution?.summary}
        testid="zone-distribution"
      >
        <ChannelList channels={distribution?.channels || []} />
      </SectionShell>
    </div>
  );
}

/* ============================ Thesis banner ============================ */
function ThesisBanner({ thesis, onRegenerate }) {
  if (!thesis) return null;
  return (
    <div data-testid="thesis-banner" style={{
      position: 'relative',
      padding: '20px 24px',
      background: `linear-gradient(135deg, ${NAVY.primary} 0%, ${NAVY.primaryStrong} 60%, #0F172A 100%)`,
      borderRadius: 16,
      color: '#FFFFFF',
      boxShadow: `0 20px 50px -22px ${NAVY.primary}77`,
      overflow: 'hidden',
    }}>
      {/* Decorative grain */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.5,
        background: 'radial-gradient(60% 80% at 100% 0%, rgba(59,130,246,0.35) 0%, transparent 60%), radial-gradient(40% 60% at 0% 100%, rgba(168,85,247,0.18) 0%, transparent 60%)',
      }} />
      <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10,
          background: 'rgba(255,255,255,0.16)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, backdropFilter: 'blur(8px)',
        }}>
          <Zap size={18} color="#FFFFFF" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 9, fontWeight: 800, letterSpacing: '0.28em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.7)', marginBottom: 4,
          }}>
            First-dollar thesis
          </div>
          <div style={{
            fontSize: 18, fontWeight: 700, lineHeight: 1.4,
            letterSpacing: '-0.01em',
            color: '#FFFFFF',
          }}>
            {thesis}
          </div>
        </div>
        {onRegenerate && (
          <button
            onClick={onRegenerate}
            data-testid="revenue-plan-regenerate"
            style={{
              padding: '7px 12px',
              background: 'rgba(255,255,255,0.14)',
              border: '1px solid rgba(255,255,255,0.25)',
              color: '#FFFFFF',
              borderRadius: 999,
              cursor: 'pointer', fontFamily: 'inherit',
              fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
              display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
              backdropFilter: 'blur(8px)',
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.22)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}
          >
            <RefreshCw size={11} /> Regenerate
          </button>
        )}
      </div>
    </div>
  );
}

/* ============================ Section shell ============================ */
function SectionShell({ index, icon, title, summary, testid, children }) {
  return (
    <section data-testid={testid} style={{
      background: NAVY.surface,
      border: `1px solid ${NAVY.border}`,
      borderRadius: 16,
      padding: '20px 22px 22px',
      boxShadow: '0 1px 2px rgba(15,15,30,0.03)',
    }}>
      <header style={{
        display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 14,
      }}>
        <div style={{
          width: 42, height: 42, borderRadius: 12,
          background: NAVY.primarySoft,
          color: NAVY.primary,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          position: 'relative',
        }}>
          {icon}
          <span style={{
            position: 'absolute', top: -8, left: -8,
            fontSize: 9, fontWeight: 800,
            fontFamily: "'JetBrains Mono', monospace",
            color: NAVY.primary, letterSpacing: '0.08em',
            padding: '2px 6px',
            background: '#FFFFFF',
            border: `1px solid ${NAVY.border}`,
            borderRadius: 6,
          }}>
            {index}
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{
            margin: 0, fontSize: 17, fontWeight: 800, letterSpacing: '-0.015em',
            color: NAVY.text, lineHeight: 1.3,
          }}>
            {title}
          </h3>
          {summary && (
            <p style={{
              margin: '4px 0 0', fontSize: 12.5,
              color: NAVY.textDim, lineHeight: 1.55,
            }}>
              {summary}
            </p>
          )}
        </div>
      </header>
      {children}
    </section>
  );
}

/* ============================ ICP segments ============================ */
function SegmentList({ segments }) {
  if (!segments.length) return <EmptyHint label="Segments will appear once the plan is generated." />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {segments.map((s, i) => (
        <div key={i} data-testid={`icp-segment-${s.rank ?? i + 1}`} style={{
          display: 'grid', gridTemplateColumns: '54px 1fr 110px', gap: 14,
          padding: '14px 16px',
          background: i === 0 ? `linear-gradient(180deg, ${NAVY.primarySoft} 0%, transparent 100%)` : NAVY.surfaceAlt,
          border: `1px solid ${i === 0 ? `${NAVY.primary}33` : NAVY.border}`,
          borderLeft: `3px solid ${i === 0 ? NAVY.primary : NAVY.border}`,
          borderRadius: 12,
        }}>
          {/* Rank */}
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
          }}>
            <div style={{
              fontSize: 10, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase',
              color: NAVY.textFaint,
            }}>
              Rank
            </div>
            <div style={{
              fontSize: 30, fontWeight: 900, lineHeight: 1,
              color: i === 0 ? NAVY.primary : NAVY.text,
              fontFamily: "'JetBrains Mono', monospace",
              letterSpacing: '-0.04em',
            }}>
              {String(s.rank ?? i + 1).padStart(2, '0')}
            </div>
          </div>

          {/* Body */}
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: 15, fontWeight: 800, color: NAVY.text,
              letterSpacing: '-0.01em', marginBottom: 8, lineHeight: 1.3,
            }}>
              {s.name || 'Untitled segment'}
            </div>
            <Grid2>
              <Field label="Pays first" value={s.pays_first} />
              <Field label="Converts fastest" value={s.converts_fastest} />
              <Field label="Urgent pain" value={s.urgent_pain} />
              <Field label="Where found" value={s.where_found} />
            </Grid2>
          </div>

          {/* Score + difficulty */}
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between',
            gap: 8,
          }}>
            <SpeedScore score={s.revenue_speed_score} />
            <DifficultyPill level={s.acq_difficulty} />
          </div>
        </div>
      ))}
    </div>
  );
}

function SpeedScore({ score }) {
  const v = Math.max(0, Math.min(100, Number(score) || 0));
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4,
    }}>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase',
        color: NAVY.textFaint,
      }}>
        Speed score
      </div>
      <div style={{
        fontSize: 22, fontWeight: 900,
        fontFamily: "'JetBrains Mono', monospace",
        color: v >= 70 ? NAVY.ok : v >= 40 ? NAVY.warn : NAVY.textDim,
        letterSpacing: '-0.04em', lineHeight: 1,
      }}>
        {v}
      </div>
      <div style={{
        width: 78, height: 4, borderRadius: 2,
        background: 'rgba(10,17,38,0.07)',
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${v}%`, height: '100%',
          background: v >= 70 ? NAVY.ok : v >= 40 ? NAVY.warn : NAVY.bad,
          transition: 'width 0.4s',
        }} />
      </div>
    </div>
  );
}

function DifficultyPill({ level }) {
  const lv = (level || '').toLowerCase();
  const cfg = lv === 'low'
    ? { bg: 'rgba(34,197,94,0.12)', fg: '#15803D', label: 'Easy acq' }
    : lv === 'high'
    ? { bg: 'rgba(239,68,68,0.12)', fg: '#B91C1C', label: 'Hard acq' }
    : { bg: 'rgba(245,158,11,0.12)', fg: '#B45309', label: 'Mid acq' };
  return (
    <span style={{
      padding: '4px 9px',
      background: cfg.bg, color: cfg.fg,
      fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
      borderRadius: 999,
    }}>
      {cfg.label}
    </span>
  );
}

/* ============================ Positioning angles ============================ */
function AngleList({ angles }) {
  if (!angles.length) return <EmptyHint label="Angles will appear once the plan is generated." />;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
      {angles.map((a, i) => (
        <div key={i} data-testid={`positioning-angle-${i}`} style={{
          position: 'relative',
          padding: '14px 16px 16px',
          background: NAVY.surfaceAlt,
          border: `1px solid ${NAVY.border}`,
          borderRadius: 12,
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{
              fontSize: 8, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase',
              padding: '3px 7px',
              background: NAVY.primary, color: '#FFFFFF',
              borderRadius: 4,
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              ANGLE {String(i + 1).padStart(2, '0')}
            </span>
            <div style={{
              fontSize: 13, fontWeight: 800, color: NAVY.text,
              letterSpacing: '-0.01em', lineHeight: 1.3,
            }}>
              {a.name || 'Untitled angle'}
            </div>
          </div>

          <blockquote style={{
            margin: 0,
            padding: '10px 12px',
            background: '#FFFFFF',
            border: `1px solid ${NAVY.border}`,
            borderLeft: `2px solid ${NAVY.primary}`,
            borderRadius: 8,
            fontSize: 13, fontWeight: 600, color: NAVY.text,
            lineHeight: 1.5,
            letterSpacing: '-0.005em',
          }}>
            {a.message ? `“${a.message}”` : 'No message yet'}
          </blockquote>

          <Field label="Pain that converts" value={a.pain_that_converts} dense />
          <Field label="Differentiation" value={a.differentiation} dense />
        </div>
      ))}
    </div>
  );
}

/* ============================ Pricing ============================ */
function PricingBlock({ pricing }) {
  const friction = pricing.friction_points || [];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 12 }}>
      <div style={{
        padding: '16px 18px',
        background: `linear-gradient(135deg, ${NAVY.primary} 0%, ${NAVY.primaryStrong} 100%)`,
        borderRadius: 12,
        color: '#FFFFFF',
        boxShadow: `0 12px 28px -12px ${NAVY.primary}77`,
      }}>
        <div style={{
          fontSize: 9, fontWeight: 800, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.65)',
        }}>
          Entry price
        </div>
        <div style={{
          fontSize: 26, fontWeight: 900, letterSpacing: '-0.025em', lineHeight: 1.15,
          marginTop: 4, marginBottom: 12,
        }}>
          {pricing.entry_price || '—'}
        </div>
        <Stack>
          <FieldLight label="Expansion path" value={pricing.expansion_path} />
          <FieldLight label="Willingness to pay" value={pricing.willingness_to_pay} />
        </Stack>
      </div>

      <div style={{
        padding: '16px 18px',
        background: NAVY.surfaceAlt,
        border: `1px solid ${NAVY.border}`,
        borderRadius: 12,
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        <div style={{
          fontSize: 10, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase',
          color: NAVY.textFaint, display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <AlertOctagon size={11} color={NAVY.warn} /> Friction to first dollar
        </div>
        {friction.length === 0 ? (
          <div style={{ fontSize: 12, color: NAVY.textDim, fontStyle: 'italic' }}>
            No frictions surfaced yet — regenerate to deepen the analysis.
          </div>
        ) : (
          <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {friction.map((f, i) => (
              <li key={i} style={{
                display: 'grid', gridTemplateColumns: '24px 1fr', gap: 10, alignItems: 'baseline',
              }}>
                <span style={{
                  width: 22, height: 22, borderRadius: '50%',
                  background: '#FFFFFF', border: `1px solid ${NAVY.border}`,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 900, color: NAVY.warn,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>{i + 1}</span>
                <span style={{ fontSize: 12.5, lineHeight: 1.5, color: NAVY.text }}>
                  {f}
                </span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

/* ============================ Distribution channels ============================ */
function ChannelList({ channels }) {
  if (!channels.length) return <EmptyHint label="Channels will appear once the plan is generated." />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {channels.map((c, i) => (
        <div key={i} data-testid={`distribution-channel-${i}`} style={{
          padding: '14px 16px',
          background: i === 0 ? `linear-gradient(180deg, ${NAVY.primarySoft} 0%, transparent 100%)` : NAVY.surfaceAlt,
          border: `1px solid ${i === 0 ? `${NAVY.primary}33` : NAVY.border}`,
          borderRadius: 12,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 10, marginBottom: 10, flexWrap: 'wrap',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{
                fontSize: 8, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase',
                padding: '3px 7px',
                background: i === 0 ? NAVY.primary : '#FFFFFF',
                color: i === 0 ? '#FFFFFF' : NAVY.textDim,
                border: i === 0 ? 'none' : `1px solid ${NAVY.border}`,
                borderRadius: 4,
                fontFamily: "'JetBrains Mono', monospace",
              }}>
                Ch {String(i + 1).padStart(2, '0')}
              </span>
              <div style={{
                fontSize: 14.5, fontWeight: 800, color: NAVY.text,
                letterSpacing: '-0.01em',
              }}>
                {c.name || 'Untitled channel'}
              </div>
            </div>
            {c.speed_to_first_lead && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '4px 10px',
                background: '#FFFFFF', border: `1px solid ${NAVY.border}`,
                borderRadius: 999,
                fontSize: 11, fontWeight: 800, color: NAVY.primary,
                fontFamily: "'JetBrains Mono', monospace",
              }}>
                <TrendingUp size={11} /> {c.speed_to_first_lead}
              </span>
            )}
          </div>

          <Grid2>
            <Field label="Where attention lives" value={c.where_attention_lives} />
            <Field label="Conversion evidence" value={c.conversion_evidence} />
            <Field label="Competitor overlap" value={c.competitor_overlap} />
            <Field label="Creator / SEO gaps" value={c.creator_seo_gaps} />
          </Grid2>
        </div>
      ))}
    </div>
  );
}

/* ============================ Atoms ============================ */
function Field({ label, value, dense }) {
  if (!value) return null;
  return (
    <div>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase',
        color: NAVY.textFaint, marginBottom: 2,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: dense ? 12 : 12.5, color: NAVY.text, lineHeight: 1.5,
      }}>
        {value}
      </div>
    </div>
  );
}

function FieldLight({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.55)', marginBottom: 2,
      }}>
        {label}
      </div>
      <div style={{ fontSize: 12.5, color: '#FFFFFF', lineHeight: 1.5, fontWeight: 500 }}>
        {value}
      </div>
    </div>
  );
}

function Grid2({ children }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12,
    }}>
      {children}
    </div>
  );
}

function Stack({ children }) {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>;
}

function EmptyHint({ label }) {
  return (
    <div style={{
      padding: '14px 16px',
      background: NAVY.surfaceAlt,
      border: `1px dashed ${NAVY.border}`,
      borderRadius: 10,
      fontSize: 12, color: NAVY.textDim, fontStyle: 'italic',
    }}>
      {label}
    </div>
  );
}

function LoadingState() {
  return (
    <div data-testid="revenue-plan-loading" style={{
      padding: '36px 28px',
      background: NAVY.surface,
      border: `1px solid ${NAVY.border}`,
      borderRadius: 16,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
      color: NAVY.text,
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: '50%',
        background: NAVY.primarySoft,
        color: NAVY.primary,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Loader2 size={20} style={{ animation: 'spin 0.9s linear infinite' }} />
      </div>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.24em', textTransform: 'uppercase',
        color: NAVY.primary,
      }}>
        Compressing time to first dollar
      </div>
      <div style={{
        fontSize: 13, color: NAVY.textDim, textAlign: 'center', maxWidth: 380, lineHeight: 1.6,
      }}>
        Synthesizing the dataroom into a 4-section revenue plan — ICP, positioning,
        pricing, distribution. This usually takes 30–60 seconds.
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div data-testid="revenue-plan-empty" style={{
      padding: '32px 28px',
      background: NAVY.surface,
      border: `1px dashed ${NAVY.border}`,
      borderRadius: 16,
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: '50%',
        background: NAVY.primarySoft, color: NAVY.primary,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Sparkles size={20} />
      </div>
      <div style={{
        fontSize: 9, fontWeight: 800, letterSpacing: '0.24em', textTransform: 'uppercase',
        color: NAVY.primary,
      }}>
        Revenue plan
      </div>
      <div style={{
        fontSize: 13, color: NAVY.textDim, textAlign: 'center', maxWidth: 420, lineHeight: 1.6,
      }}>
        Generate the dataroom first (&quot;Show the docs&quot;) — the revenue plan synthesizes
        those docs into 4 strategic sections focused on revenue speed.
      </div>
    </div>
  );
}
